import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../../utils/api';
import useDebounce from '../../hooks/useDebounce';
import MatchCard from './MatchCard';
import { MatchesListSkeleton } from '../common/Skeleton';
import './MatchesList.css';

/**
 * Recent-matches grid with client-side map + team-name filtering (Fas 11B).
 *
 * Fetches `/api/matches` once and derives the map dropdown from the returned
 * rows (each match's `mapname` plus any per-map entries in `maps[]`), so the
 * options always reflect what has actually been played. The team search matches
 * either side's name (debounced). Player-level filtering is intentionally
 * omitted — the match-list payload carries team names, not rosters.
 *
 * @param {object} props
 * @param {(matchid: number) => void} props.onSelectMatch - Open a match detail.
 * @param {Set<number>} [props.liveMatchIds] - Ids currently live (badge).
 * @returns {JSX.Element}
 */
/** League type filter pills (only shown when league matches exist). */
const TYPE_FILTERS = [
  { key: 'all', labelKey: 'matches.all' },
  { key: 'league', prefix: '🏅 ', labelKey: 'matches.league' },
  { key: 'normal', labelKey: 'matches.normal' },
];

/** Game-mode sub-filter for league matches. */
const LEAGUE_MODE_FILTERS = [
  { key: 'all', labelKey: 'matches.allModes' },
  { key: 'competitive', label: '5v5' },
  { key: 'wingman', label: '2v2' },
  { key: 'duel', label: '1v1' },
];

const MatchesList = ({ onSelectMatch, liveMatchIds }) => {
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapFilter, setMapFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [modeFilter, setModeFilter] = useState('all');
  const [teamSearch, setTeamSearch] = useState('');
  const debouncedTeam = useDebounce(teamSearch, 150);

  useEffect(() => {
    fetch(`${API_URL}/api/matches`)
      .then(res => res.json())
      .then(data => {
        setMatches(data.matches || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Distinct maps actually present across the loaded matches.
  const mapOptions = useMemo(() => {
    const set = new Set();
    matches.forEach((m) => {
      if (m.mapname) set.add(m.mapname);
      (Array.isArray(m.maps) ? m.maps : []).forEach((mp) => mp.mapname && set.add(mp.mapname));
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [matches]);

  const hasLeagueMatches = useMemo(() => matches.some((m) => m.league_info), [matches]);

  const filtered = useMemo(() => {
    const q = debouncedTeam.trim().toLowerCase();
    return matches.filter((m) => {
      if (typeFilter === 'league' && !m.league_info) return false;
      if (typeFilter === 'normal' && m.league_info) return false;
      if (typeFilter === 'league' && modeFilter !== 'all'
          && m.league_info?.game_mode !== modeFilter) return false;
      if (mapFilter !== 'all') {
        const maps = [m.mapname, ...(Array.isArray(m.maps) ? m.maps.map((x) => x.mapname) : [])];
        if (!maps.includes(mapFilter)) return false;
      }
      if (q) {
        const names = `${m.team1_name || ''} ${m.team2_name || ''}`.toLowerCase();
        if (!names.includes(q)) return false;
      }
      return true;
    });
  }, [matches, mapFilter, typeFilter, modeFilter, debouncedTeam]);

  const filtersActive = mapFilter !== 'all' || typeFilter !== 'all'
    || modeFilter !== 'all' || debouncedTeam.trim();

  if (loading) return (
    <div className="matches-list">
      <h2 className="section-heading">{t('dashboard.latestMatches')}</h2>
      <p className="matches-hint">{t('matches.clickHint')}</p>
      <MatchesListSkeleton />
    </div>
  );

  return (
    <div className="matches-list">
      <h2 className="section-heading">{t('dashboard.latestMatches')}</h2>
      <p className="matches-hint">{t('matches.clickHint')}</p>
      {hasLeagueMatches && (
        <div className="matches-type-filter">
          {TYPE_FILTERS.map((tf) => (
            <button
              key={tf.key}
              type="button"
              className={`matches-type-btn ${typeFilter === tf.key ? 'active' : ''}`}
              onClick={() => { setTypeFilter(tf.key); if (tf.key !== 'league') setModeFilter('all'); }}
            >
              {(tf.prefix || '') + (tf.labelKey ? t(tf.labelKey) : tf.label)}
            </button>
          ))}
          {typeFilter === 'league' && (
            <div className="matches-mode-filter">
              {LEAGUE_MODE_FILTERS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`matches-type-btn ${modeFilter === m.key ? 'active' : ''}`}
                  onClick={() => setModeFilter(m.key)}
                >
                  {m.labelKey ? t(m.labelKey) : m.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="matches-filters">
        <input
          type="text"
          className="matches-search"
          placeholder={`🔎 ${t('matches.searchTeams')}`}
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          aria-label={t('matches.searchTeams')}
        />
        <select
          className="matches-filter-select"
          value={mapFilter}
          onChange={(e) => setMapFilter(e.target.value)}
          aria-label={t('matches.mapFilterAria')}
        >
          <option value="all">{t('matches.allMaps')}</option>
          {mapOptions.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        {filtersActive && (
          <button
            type="button"
            className="matches-filter-clear"
            onClick={() => { setMapFilter('all'); setTeamSearch(''); setTypeFilter('all'); setModeFilter('all'); }}
          >
            Rensa
          </button>
        )}
        <span className="matches-filter-count">{filtered.length} av {matches.length}</span>
      </div>
      {filtered.length === 0 ? (
        <div className="matches-empty">{t('matches.noMatch')}</div>
      ) : (
        <div className="matches-grid">
          {filtered.map(match => (
            <MatchCard
              key={match.matchid}
              match={match}
              onClick={onSelectMatch}
              isLive={liveMatchIds && liveMatchIds.has(match.matchid)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MatchesList;
